import { Component, EventEmitter, inject, Input, OnInit, Output, ViewChild } from '@angular/core';

import { translate } from '@jsverse/transloco';

import { WidgetAnalysisGroupDataSource } from 'app/modules/widget/components/widget-data-source.component';
import { AbstractChartReport } from 'app/modules/widget/services/chart-report/chart-report-abstract/chart-report-abstract';
import { SectionMetadata } from 'app/providers/storage-api/collection.service';
import { LoggerService } from 'app/services/logger.service';
import {
  injectComponentTelemetryService,
  EnableComponentTelemetry,
  EnableClassTelemetry,
} from 'app/services/otel.service';
import { SectionService } from 'app/services/section.service';
import { FilterUtils } from 'app/utils/filter.utils';
import { Widget } from '../../../../../providers/bridge-api/dashboard.service';
import { AnalyticItemTypeMap } from '../../../../dashboard/services/analytic-item-types';
import { CrosstabWidgetOptions } from '../widget-crosstab.component';
import {
  AnalyticItemsDialogOptions,
  AnalyticItemSelectorComponent,
} from '../../analytic-item-selector/analytic-item-selector.component';
import { SelectedSection } from '../../section-selector/section-selector.component';

@EnableClassTelemetry()
@Component({
  selector: 'app-crosstab-analytic-items-dialog',
  templateUrl: './crosstab-analytic-items-dialog.component.html',
  styleUrls: ['./crosstab-analytic-items-dialog.component.scss'],
  standalone: false,
})
export class CrosstabAnalyticItemsDialogComponent implements OnInit {
  @EnableComponentTelemetry() otelService = injectComponentTelemetryService();
  @ViewChild('rowAnalyticItemSelector') rowSelector: AnalyticItemSelectorComponent;
  @ViewChild('columnAnalyticItemSelector') columnSelector: AnalyticItemSelectorComponent;
  @Input() options: AnalyticItemsDialogOptions;
  @Output() modalClosed: EventEmitter<any> = new EventEmitter<any>();

  private sectionService = inject(SectionService);
  private logger = inject(LoggerService);

  analyticItemTypeKeys: string[];

  widget: Widget<CrosstabWidgetOptions>;
  widgetDataSource: WidgetAnalysisGroupDataSource;
  report: AbstractChartReport;
  analyticItemTypes: AnalyticItemTypeMap;

  originalRowAnalyticItem: string;
  originalRowSubTypes: string[];

  originalColumnAnalyticItem: string;
  originalColumnSubTypes: string[];

  rowAnalyticItem: string;
  rowSubTypes: string[];
  rowCustomFieldSelected: boolean;
  rowStandardFieldSelected: boolean;
  rowSectionPropertySelected: boolean;
  rowIsSectionSelectorAvailable: boolean;
  rowSelectionText: string;
  rowSelectedSections: SelectedSection[];

  columnAnalyticItem: string;
  columnSubTypes: string[];
  columnCustomFieldSelected: boolean;
  columnStandardFieldSelected: boolean;
  columnSectionPropertySelected: boolean;
  columnIsSectionSelectorAvailable: boolean;
  columnSelectionText: string;
  columnSelectedSections: SelectedSection[];

  sectionMetadata: SectionMetadata[] = [];
  sectionSelectionText: string;
  invalidSelectionTooltip: string;
  isValid = false;
  isLoading = false;

  constructor() {}

  ngOnInit() {
    this.widget = this.options.widget;
    this.widgetDataSource = this.options.widgetDataSource;
    this.report = this.options.report;
    this.analyticItemTypes = this.options.analyticItemTypes;
    this.analyticItemTypeKeys = Object.keys(this.options.analyticItemTypes);

    this.rowAnalyticItem = this.options.widget.payload.analyticItem;
    this.originalRowAnalyticItem = this.rowAnalyticItem;
    this.rowSubTypes = this.options.widget.payload.entityTypeFilter
      ? this.options.widget.payload.entityTypeFilter.split(',')
      : [];
    this.originalRowSubTypes = [...this.rowSubTypes];
    this.rowCustomFieldSelected = this.options.widget.payload.customFieldSelected || false;
    this.rowStandardFieldSelected = this.options.widget.payload.standardFieldSelected || false;
    this.rowSectionPropertySelected = this.options.widget.payload.sectionPropertySelected || false;
    this.rowSelectedSections = this.options.widget.payload.rowSelectedSections;

    this.columnAnalyticItem =
      this.options.widget.payload.columnAnalyticItem || this.rowAnalyticItem;
    this.originalColumnAnalyticItem = this.columnAnalyticItem;
    this.columnSubTypes = this.options.widget.payload.columnEntityTypeFilter
      ? this.options.widget.payload.columnEntityTypeFilter.split(',')
      : (!this.options.widget.payload.columnAnalyticItem && [...this.rowSubTypes]) || [];
    this.originalColumnSubTypes = [...this.columnSubTypes];
    this.columnCustomFieldSelected = this.options.widget.payload.columnCustomFieldSelected || false;
    this.columnStandardFieldSelected =
      this.options.widget.payload.columnStandardFieldSelected || false;
    this.columnSectionPropertySelected =
      this.options.widget.payload.columnSectionPropertySelected || false;
    this.columnSelectedSections = this.options.widget.payload.columnSelectedSections;

    if (this.widgetDataSource.hasSections) {
      this.initSectionSelector();
    }
  }

  async initSectionSelector() {
    await this.loadSectionMetadata();
    this.setSectionSelectorAvailable();
  }

  private async loadSectionMetadata(): Promise<void> {
    this.isLoading = true;

    try {
      const sectionMetadataList = await this.sectionService.listSectionMetadata(
        this.widget.payload.projectId,
        this.widgetDataSource
      );

      this.sectionMetadata.push(...sectionMetadataList);

      const sectionPropertiesList = await this.sectionService.loadSectionProperties(
        this.widget.payload.projectId,
        this.widgetDataSource
      );

      this.sectionMetadata.push(...sectionPropertiesList);

      // Add section properties with cardinality > 0 to sectionProperties
      const cardinalityListResult = await this.sectionService.getSectionPropertyCardinality(
        this.widget.payload.projectId,
        this.widgetDataSource
      );

      const validSectionProperties = cardinalityListResult.items
        .filter((item) => item.cardinality > 0 && !item.fieldName.includes('sectionTime'))
        .map((item) => item.fieldName);

      // Clear and repopulate to avoid duplicates when dialog reopens
      if (!this.analyticItemTypes.sectionProperties.subTypes) {
        this.analyticItemTypes.sectionProperties.subTypes = [];
      }
      this.analyticItemTypes.sectionProperties.subTypes = [...validSectionProperties];
    } catch (error) {
      this.logger.error(translate('widgets.analytic-item-selector.failed-to-load-sections'), {
        error,
      });
    }

    this.isLoading = false;
  }

  private setSectionSelectorAvailable(): void {
    this.rowIsSectionSelectorAvailable = FilterUtils.isSectionSelectorAvailable(
      this.rowAnalyticItem
    );
    this.columnIsSectionSelectorAvailable = FilterUtils.isSectionSelectorAvailable(
      this.columnAnalyticItem
    );

    if (!this.rowIsSectionSelectorAvailable) {
      this.rowSelectedSections = undefined;
    }
    if (!this.columnIsSectionSelectorAvailable) {
      this.columnSelectedSections = undefined;
    }
  }

  onAnalyticItemChange(obj: { item: string; tooltip: string }, type: 'row' | 'column') {
    if (type === 'row') {
      this.rowAnalyticItem = obj.item;
      this.rowSelectionText = obj.tooltip;
      // Ensure section property flag is set based on the analytic item
      if (obj.item?.startsWith('sectionProperties.')) {
        this.rowSectionPropertySelected = true;
      }
    } else {
      this.columnAnalyticItem = obj.item;
      this.columnSelectionText = obj.tooltip;
      // Ensure section property flag is set based on the analytic item
      if (obj.item?.startsWith('sectionProperties.')) {
        this.columnSectionPropertySelected = true;
      }
    }
    this.setSectionSelectorAvailable();
    setTimeout(() => this.validate());
  }

  onSectionSelected(selectedSections: SelectedSection[], type: 'row' | 'column'): void {
    if (type === 'row') {
      this.rowSelectedSections = selectedSections;
      this.sectionSelectionText = undefined;

      if (this.rowSelectedSections?.some((sec) => !sec.buckets.length)) {
        this.sectionSelectionText = translate('widgets.analytic-item-selector.must-select-section');
      }
    } else {
      this.columnSelectedSections = selectedSections;
      this.sectionSelectionText = undefined;

      if (this.columnSelectedSections?.some((sec) => !sec.buckets.length)) {
        this.sectionSelectionText = translate('widgets.analytic-item-selector.must-select-section');
      }
    }

    this.validate();
  }

  validate(): void {
    this.isValid = this.checkValidity();
    this.invalidSelectionTooltip = this.assembleInvalidSelectionTooltipText();
  }

  checkValidity(): boolean {
    if (
      this.rowIsSectionSelectorAvailable &&
      this.rowSelectedSections &&
      (this.rowSelectedSections.some((sec) => !sec.field) ||
        this.rowSelectedSections.some((sec) => !sec.buckets.length))
    ) {
      return false;
    }
    if (
      this.columnIsSectionSelectorAvailable &&
      this.columnSelectedSections &&
      (this.columnSelectedSections.some((sec) => !sec.field) ||
        this.columnSelectedSections.some((sec) => !sec.buckets.length))
    ) {
      return false;
    }
    return this.rowSelector?.hasSelection && this.columnSelector?.hasSelection;
  }

  assembleInvalidSelectionTooltipText(): string {
    if (this.isValid) {
      return undefined;
    }
    if (this.sectionSelectionText) {
      return this.sectionSelectionText;
    }
    if (this.rowSelectionText) {
      return this.rowSelectionText;
    }
    if (this.columnSelectionText) {
      return this.columnSelectionText;
    }
    return undefined;
  }

  cancel() {
    this.modalClosed.emit(null);
  }

  apply() {
    this.modalClosed.emit({
      row: {
        analyticItem: this.rowAnalyticItem,
        subTypes: this.rowSubTypes.sort().join(),
        customField: this.rowCustomFieldSelected,
        standardField: this.rowStandardFieldSelected,
        sectionProperty: this.rowSectionPropertySelected,
        selectedSections: this.rowSelectedSections,
      },
      column: {
        analyticItem: this.columnAnalyticItem,
        subTypes: this.columnSubTypes.sort().join(),
        customField: this.columnCustomFieldSelected,
        standardField: this.columnStandardFieldSelected,
        sectionProperty: this.columnSectionPropertySelected,
        selectedSections: this.columnSelectedSections,
      },
    });
  }
}
